// Statistics helpers shared between DistributionChart.jsx (curve rendering)
// and keyInsights.js (Key Insights card text). Pulled out of
// DistributionChart.jsx on 2026-08-05 so the Key Insights computation can
// reuse the exact same Gaussian fit / percentile-schema logic instead of a
// second, drifting copy - same "one source of truth" principle as the
// earlier cost-band-detail fetch consolidation.
//
// Originally ported from statistics.js (erf / probit / Gaussian fit) - see
// DistributionChart.jsx's git history for the original inline copy and its
// derivation notes. No behavior changed in this move, verbatim extraction.

// South Africa (territory_id 2) uses P10/P30/P40/P50/P90 instead of the
// standard P10/P40/P50/P60/P90 - confirmed against the real schema.
export function percentilesFor(territoryId) {
  return territoryId === 2
    ? { min: 10, ntr: 30, exp: 40, vexp: 50, max: 90 }
    : { min: 10, ntr: 40, exp: 50, vexp: 60, max: 90 };
}

function snProbit(p) {
  if (p <= 0 || p >= 1) throw new Error(`snProbit: p must be in (0,1), got ${p}`);
  const a = [-3.969683028665376e+01, 2.209460984245205e+02,
    -2.759285104469687e+02, 1.383577518672690e+02,
    -3.066479806614716e+01, 2.506628277459239e+00];
  const b = [-5.447609879822406e+01, 1.615858368580409e+02,
    -1.556989798598866e+02, 6.680131188771972e+01,
    -1.328068155288572e+01];
  const c = [-7.784894002430293e-03, -3.223964580411365e-01,
    -2.400758277161838e+00, -2.549732539343734e+00,
    4.374664141464968e+00, 2.938163982698783e+00];
  const d = [7.784695709041462e-03, 3.224671290700398e-01,
    2.445134137142996e+00, 3.754408661907416e+00];
  const pLow = 0.02425, pHigh = 1 - pLow;
  let q, r;
  if (p < pLow) {
    q = Math.sqrt(-2 * Math.log(p));
    return (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
      ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
  } else if (p <= pHigh) {
    q = p - 0.5; r = q * q;
    return (((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q /
      (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1);
  } else {
    q = Math.sqrt(-2 * Math.log(1 - p));
    return -(((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
      ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1);
  }
}

export function gaussianPdf(x, mu, sigma) {
  const z = (x - mu) / sigma;
  return Math.exp(-0.5 * z * z) / (sigma * Math.sqrt(2 * Math.PI));
}

// A valid Gaussian fit needs its 5 percentile points to actually increase
// in value with percentile. Real data can violate that (e.g. South Africa,
// where Max/P90 is below Vexp/P50) - which drives the least-squares fit's
// sigma negative/degenerate.
export function isMonotonic(d) {
  return d.min <= d.ntr && d.ntr <= d.exp && d.exp <= d.vexp && d.vexp <= d.max;
}

// Least-squares fit of a Gaussian to the 5 reported percentile points.
// d must have { min, ntr, exp, vexp, max } values and a `percentiles`
// object with matching percentile labels (10/40/50/60/90, or the
// South-Africa-shifted 10/30/40/50/90).
export function fitGaussian(d, p) {
  const points = [
    { p: p.min, v: d.min },
    { p: p.ntr, v: d.ntr },
    { p: p.exp, v: d.exp },
    { p: p.vexp, v: d.vexp },
    { p: p.max, v: d.max },
  ].map((pt) => ({ z: snProbit(pt.p / 100), v: pt.v }));

  const n = points.length;
  const sumZ = points.reduce((s, pt) => s + pt.z, 0);
  const sumV = points.reduce((s, pt) => s + pt.v, 0);
  const sumZV = points.reduce((s, pt) => s + pt.z * pt.v, 0);
  const sumZZ = points.reduce((s, pt) => s + pt.z * pt.z, 0);

  const sigma = (n * sumZV - sumZ * sumV) / (n * sumZZ - sumZ * sumZ);
  const mu = (sumV - sigma * sumZ) / n;
  return { mu, sigma };
}

// √n confidence scaling - tallest-sample territory gets factor 1.0. Uses
// full_range_ee_count (our field name from /api/cost-band-detail).
export function nFactor(d, maxN) {
  if (!d.full_range_ee_count) return 1.0;
  return Math.sqrt(d.full_range_ee_count) / Math.sqrt(maxN);
}

export function maxNFor(data) {
  const ns = data.map((d) => d.full_range_ee_count || 0).filter((n) => n > 0);
  if (ns.length === 0) return 1;
  return Math.max(...ns);
}

// A row's fit is only meaningful if its percentiles are monotonic and the
// resulting sigma is positive - same guard DistributionChart.jsx uses
// before drawing a real curve for a row. Returns null for a degenerate row
// instead of a fabricated/inverted fit.
export function fitGaussianSafe(d) {
  if (!isMonotonic(d)) return null;
  const fit = fitGaussian(d, percentilesFor(d.territory_id));
  if (!(fit.sigma > 0)) return null;
  return fit;
}
